import React, { type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import { type V2ApptOutcome } from 'vl-common/src/schemas/getMetaAppointments';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { confirmationModal, errorModal } from 'vl-common/src/components/Modals';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionOutcomeMutation } from '@hooks/useSessionOutcomeMutation';
import { useClearSessionFormKeys } from '@hooks/useClearSessionForm';
import { useAppointmentPrescriptions } from '@hooks/useAppointmentPrescriptions';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import { SessionFormItem } from './SessionFormItem';

export const AppointmentOutcomeFormItem = () => {
  const { t } = useTranslation('common');
  const appt_id = useSessionAppointmentId();
  const { appointment } = useSessionAppointment();
  const { detailsCurrentAppointment } = useAppointmentDetails(appt_id);

  const { data: appointmentMeta } = useAppointmentMeta();
  const { data: eligibility } = useEligibilityChecks();

  const { outcome, setOutcome, selectedActions, setSelectedActions, availableActions } = useSessionOutcome();
  const { debouncedMutate, mutateAsync } = useSessionOutcomeMutation();

  const clearSessionFormKeys = useClearSessionFormKeys();

  const { updatePrescribeMedication } = useAppointmentPrescriptions();

  if (appointmentMeta?.appt_version === 1) {
    return (
      <SessionFormItem name="appointment_outcome" label={t('Appointment Outcome')} required linkAria>
        <Select
          disabled={appointment?.appointment_completed || appointment?.read_only}
          placeholder={t('Select or Type to search')}
          defaultValue={outcome}
          onSelect={setOutcome}
          onBlur={() => debouncedMutate({ continueOnValidationError: true })}
          options={appointmentMeta?.appt_outcomes?.map((outcome, index) => ({
            key: index,
            value: outcome.appt_outcome_code,
            label: outcome.appt_outcome_desc,
            disabled:
              (outcome?.appt_f2f_consultant_flag || outcome?.appt_f2f_physio_flag || outcome?.appt_ireq_flag) &&
              eligibility?.VIRTUAL_ONLY
          }))}
          data-testid="appointment-outcome-select"
        />
      </SessionFormItem>
    );
  }

  if (appointmentMeta?.appt_version === 2) {
    const confirmSelection: ComponentProps<typeof Select<typeof outcome>>['onChange'] = (v) => {
      if (v === outcome) return;

      if (detailsCurrentAppointment?.appt_version === 1) {
        setOutcome(v);
        debouncedMutate({ outcome_code: v, continueOnValidationError: true });
        return;
      }

      const newOutcomeActions =
        appointmentMeta?.appt_outcomes?.find((outcome) => outcome.appt_outcome_code === v)?.appt_actions || [];

      if (!outcome) {
        setOutcome(v);

        setSelectedActions?.(
          newOutcomeActions.filter(({ mandatory }) => mandatory).map(({ appt_action_code }) => ({ appt_action_code }))
        );

        debouncedMutate({ outcome_code: v, continueOnValidationError: true });
        return;
      }

      const actionsThatWillBeRemoved =
        selectedActions.filter((action) => {
          const isEnabled = newOutcomeActions.find(
            (newAction) => newAction.appt_action_code === action.appt_action_code
          )?.enabled;

          const isMandatory = newOutcomeActions.find(
            (newAction) => newAction.appt_action_code === action.appt_action_code
          )?.mandatory;

          return !isEnabled || (isEnabled && !isMandatory);
        }) || [];

      const verboseActionsThatWillBeRemoved =
        availableActions?.filter((action) =>
          actionsThatWillBeRemoved.find((a) => a.appt_action_code === action.appt_action_code)
        ) || [];

      const actionsThatWillBeRetained = selectedActions.filter((action) => !actionsThatWillBeRemoved.includes(action));
      const verboseActionsThatWillBeRetained = availableActions?.filter((action) =>
        actionsThatWillBeRetained.find((a) => a.appt_action_code === action.appt_action_code)
      );

      const actionsThatWillBeAdded =
        newOutcomeActions.filter((a) => {
          const isMandatory = a?.mandatory;
          const isSelected = selectedActions.find((action) => action.appt_action_code === a.appt_action_code);

          return isMandatory && !isSelected;
        }) || [];

      confirmationModal({
        title: `Are you sure you want to change the outcome to ${
          appointmentMeta?.appt_outcomes?.find((outcome) => outcome.appt_outcome_code === v)?.appt_outcome_desc
        }?`,
        content: (
          <>
            {verboseActionsThatWillBeRemoved?.length > 0 && (
              <p>
                {t('The ')}
                {formatActionList(verboseActionsThatWillBeRemoved)} {t(' will be removed.')}
              </p>
            )}

            {actionsThatWillBeRetained?.length > 0 && (
              <p>
                {t('The ')}
                {formatActionList(verboseActionsThatWillBeRetained)} {t(' will be retained.')}
              </p>
            )}

            {actionsThatWillBeAdded?.length > 0 && (
              <p>
                {t('The ')}
                {formatActionList(actionsThatWillBeAdded)} {t(' will be added.')}
              </p>
            )}
          </>
        ),
        okButtonProps: { 'data-testid': 'change-outcome-ok-button' },
        onOk: async () => {
          const appt_actions = [
            ...actionsThatWillBeRetained,
            ...actionsThatWillBeAdded.map(({ appt_action_code }) => ({ appt_action_code }))
          ];

          const response = await mutateAsync({
            outcome_code: v,
            appt_actions,
            continueOnValidationError: true
          });

          if (response?.error) {
            errorModal({
              title: t('Failed to change appointment outcome'),
              content: response.error?.message || t('Something went wrong. Please try again.')
            });
            return;
          }

          setOutcome(v);

          for (const { appt_action_code } of actionsThatWillBeRemoved) {
            clearSessionFormKeys(appt_action_code);
            if (appt_action_code === 'PRESCR') {
              updatePrescribeMedication(false);
            }
          }

          setSelectedActions?.(appt_actions);
        },
        cancelButtonProps: { 'data-testid': 'change-outcome-cancel-button' }
      });
    };

    return (
      <SessionFormItem label={t('Appointment Outcome')} required linkAria>
        <Select
          disabled={appointment?.appointment_completed || appointment?.read_only}
          placeholder={t('Select or Type to search')}
          defaultValue={outcome}
          onSelect={confirmSelection}
          value={outcome}
          options={appointmentMeta?.appt_outcomes?.map((outcome) => ({
            value: outcome.appt_outcome_code,
            label: outcome.appt_outcome_desc,
            disabled:
              !!(eligibility?.VIRTUAL_ONLY && outcome.appt_outcome_code === 'V2INV') ||
              !!(
                eligibility?.VIRTUAL_ONLY &&
                outcome.appt_actions?.find((action) => action.appt_action_code === 'REFF2F')?.mandatory
              )
          }))}
          data-testid="appointment-outcome-select"
          allowClear
          onClear={() => setOutcome(undefined)}
        />
      </SessionFormItem>
    );
  }

  return null;
};

function formatCommaList(list: string[]) {
  if (!list) return '';

  if (list.length === 1) return list[0];

  if (list.length === 2) return `${list[0]} and ${list[1]}`;

  return `${list.slice(0, list.length - 1).join(', ')} and ${list[list.length - 1]}`;
}

function formatActionList(actions: V2ApptOutcome['appt_actions']) {
  if (!actions) return '';
  return `${formatCommaList(actions.map((action) => action.appt_action_desc))} ${
    actions.length > 1 ? 'actions' : 'action'
  }`;
}
